import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { useToast } from '../components/ui/ToastContainer';
import { Sitemap } from '../components/Sitemap';
import { BookIcon, CheckCircleIcon, XIcon, ArrowRightIcon, ArrowLeftIcon, HelpCircleIcon, ClockIcon, BarChart2Icon, BookmarkIcon, VolumeIcon } from 'lucide-react';
import { Tooltip } from '../components/ui/Tooltip';
import { FlashcardPractice } from '../components/FlashcardPractice';
import { WordMatchingGame } from '../components/WordMatchingGame';
import { CountingQuiz } from '../components/CountingQuiz';
// Mock exercise data - in a real app, this would come from an API
const EXERCISES = {
  'multiple-choice-1': {
    id: 'multiple-choice-1',
    title: {
      en: 'Basic Vocabulary Test',
      ar: 'اختبار المفردات الأساسية'
    },
    description: {
      en: 'Test your knowledge of essential English vocabulary with this multiple choice exercise.',
      ar: 'اختبر معرفتك بالمفردات الإنجليزية الأساسية مع هذا التمرين متعدد الخيارات.'
    },
    difficulty: 'Beginner',
    difficultyAr: 'مبتدئ',
    type: 'Multiple Choice',
    typeAr: 'اختيار من متعدد',
    questions: 10,
    timeEstimate: '5-10',
    progress: 0,
    isCompleted: false,
    content: {
      questions: [{
        id: 1,
        question: {
          en: 'What is the meaning of "Hello"?',
          ar: 'ما معنى كلمة "Hello"؟'
        },
        options: [{
          id: 'a',
          text: {
            en: 'Goodbye',
            ar: 'وداعاً'
          }
        }, {
          id: 'b',
          text: {
            en: 'Thank you',
            ar: 'شكراً'
          }
        }, {
          id: 'c',
          text: {
            en: 'Welcome',
            ar: 'مرحباً'
          }
        }, {
          id: 'd',
          text: {
            en: 'Sorry',
            ar: 'آسف'
          }
        }],
        correctAnswer: 'c',
        explanation: {
          en: '"Hello" is a greeting used when meeting someone.',
          ar: '"Hello" هي تحية تستخدم عند مقابلة شخص ما.'
        }
      }, {
        id: 2,
        question: {
          en: 'What is the meaning of "Thank you"?',
          ar: 'ما معنى عبارة "Thank you"؟'
        },
        options: [{
          id: 'a',
          text: {
            en: 'Please',
            ar: 'من فضلك'
          }
        }, {
          id: 'b',
          text: {
            en: 'Thanks',
            ar: 'شكراً'
          }
        }, {
          id: 'c',
          text: {
            en: 'Sorry',
            ar: 'آسف'
          }
        }, {
          id: 'd',
          text: {
            en: 'Excuse me',
            ar: 'عفواً'
          }
        }],
        correctAnswer: 'b',
        explanation: {
          en: '"Thank you" is used to express gratitude.',
          ar: '"Thank you" تستخدم للتعبير عن الامتنان.'
        }
      }, {
        id: 3,
        question: {
          en: 'What is the meaning of "Goodbye"?',
          ar: 'ما معنى كلمة "Goodbye"؟'
        },
        options: [{
          id: 'a',
          text: {
            en: 'Hello',
            ar: 'مرحباً'
          }
        }, {
          id: 'b',
          text: {
            en: 'Welcome',
            ar: 'أهلاً وسهلاً'
          }
        }, {
          id: 'c',
          text: {
            en: 'Farewell',
            ar: 'وداعاً'
          }
        }, {
          id: 'd',
          text: {
            en: 'Sorry',
            ar: 'آسف'
          }
        }],
        correctAnswer: 'c',
        explanation: {
          en: '"Goodbye" is used when leaving or ending a conversation.',
          ar: '"Goodbye" تستخدم عند المغادرة أو إنهاء المحادثة.'
        }
      }]
    }
  },
  'fill-blank-1': {
    id: 'fill-blank-1',
    title: {
      en: 'Present Tense Practice',
      ar: 'تدريب على زمن المضارع'
    },
    description: {
      en: 'Practice using the present tense with this fill-in-the-blank exercise.',
      ar: 'تدرب على استخدام زمن المضارع مع هذا التمرين لملء الفراغات.'
    },
    difficulty: 'Intermediate',
    difficultyAr: 'متوسط',
    type: 'Fill in the Blanks',
    typeAr: 'املأ الفراغات',
    questions: 8,
    timeEstimate: '10-15',
    progress: 25,
    isCompleted: false,
    content: {
      questions: [{
        id: 1,
        question: {
          en: 'She ____ (work) in a hospital.',
          ar: 'هي ____ (تعمل) في مستشفى.'
        },
        answer: 'works',
        explanation: {
          en: 'For third-person singular (she), we add -s to the verb in simple present.',
          ar: 'للشخص الثالث المفرد (هي)، نضيف -s إلى الفعل في المضارع البسيط.'
        }
      }, {
        id: 2,
        question: {
          en: 'They ____ (study) English every day.',
          ar: 'هم ____ (يدرسون) الإنجليزية كل يوم.'
        },
        answer: 'study',
        explanation: {
          en: 'For plural subjects (they), we use the base form of the verb in simple present.',
          ar: 'للفاعل الجمع (هم)، نستخدم الشكل الأساسي للفعل في المضارع البسيط.'
        }
      }, {
        id: 3,
        question: {
          en: 'I ____ (be) a student.',
          ar: 'أنا ____ (أكون) طالباً.'
        },
        answer: 'am',
        explanation: {
          en: 'The verb "to be" is irregular. For "I", we use "am".',
          ar: 'الفعل "to be" غير منتظم. لـ "أنا"، نستخدم "am".'
        }
      }]
    }
  },
  'matching-1': {
    id: 'matching-1',
    title: {
      en: 'Match Words and Meanings',
      ar: 'طابق الكلمات ومعانيها'
    },
    description: {
      en: 'Test your vocabulary by matching English words with their Arabic translations.',
      ar: 'اختبر مفرداتك من خلال مطابقة الكلمات الإنجليزية مع ترجماتها العربية.'
    },
    difficulty: 'Beginner',
    difficultyAr: 'مبتدئ',
    type: 'Matching',
    typeAr: 'مطابقة',
    questions: 10,
    timeEstimate: '5',
    progress: 0,
    isCompleted: false,
    content: {
      type: 'word-matching'
    }
  },
  'flashcards-1': {
    id: 'flashcards-1',
    title: {
      en: 'Common Phrases Flashcards',
      ar: 'بطاقات العبارات الشائعة'
    },
    description: {
      en: 'Learn common English phrases with these interactive flashcards.',
      ar: 'تعلم العبارات الإنجليزية الشائعة مع بطاقات التعلم التفاعلية هذه.'
    },
    difficulty: 'Beginner',
    difficultyAr: 'مبتدئ',
    type: 'Flashcards',
    typeAr: 'بطاقات تعليمية',
    questions: 15,
    timeEstimate: '10',
    progress: 0,
    isCompleted: false,
    content: {
      type: 'flashcards'
    }
  },
  'counting-1': {
    id: 'counting-1',
    title: {
      en: 'Number Recognition',
      ar: 'التعرف على الأرقام'
    },
    description: {
      en: 'Practice counting and number recognition in English.',
      ar: 'تدرب على العد والتعرف على الأرقام باللغة الإنجليزية.'
    },
    difficulty: 'Beginner',
    difficultyAr: 'مبتدئ',
    type: 'Interactive',
    typeAr: 'تفاعلي',
    questions: 10,
    timeEstimate: '5-10',
    progress: 0,
    isCompleted: false,
    content: {
      type: 'counting-quiz'
    }
  }
};
export function ExerciseDetail() {
  const {
    isArabic
  } = useLanguage();
  const {
    exerciseId
  } = useParams<{
    exerciseId: string;
  }>();
  const navigate = useNavigate();
  const toast = useToast();
  const [exercise, setExercise] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [userAnswers, setUserAnswers] = useState<Record<number, string>>({});
  const [showResults, setShowResults] = useState(false);
  const [isBookmarked, setIsBookmarked] = useState(false);
  const [timeSpent, setTimeSpent] = useState(0);
  const [timer, setTimer] = useState<NodeJS.Timeout | null>(null);
  // Fetch exercise data
  useEffect(() => {
    // In a real app, this would be an API call
    setLoading(true);
    if (exerciseId && EXERCISES[exerciseId as keyof typeof EXERCISES]) {
      setExercise(EXERCISES[exerciseId as keyof typeof EXERCISES]);
    } else {
      // Handle exercise not found
      toast.error(isArabic ? 'لم يتم العثور على التمرين' : 'Exercise not found', isArabic ? 'لا يمكن العثور على التمرين المطلوب.' : 'The requested exercise could not be found.');
      navigate('/learn/practice');
    }
    setLoading(false);
    // Start timer
    const intervalId = setInterval(() => {
      setTimeSpent(prev => prev + 1);
    }, 1000);
    setTimer(intervalId);
    return () => {
      if (intervalId) clearInterval(intervalId);
    };
  }, [exerciseId, navigate, toast, isArabic]);
  const handleAnswerSelect = (questionId: number, answer: string) => {
    setUserAnswers(prev => ({
      ...prev,
      [questionId]: answer
    }));
  };
  const handleTextAnswerChange = (questionId: number, answer: string) => {
    setUserAnswers(prev => ({
      ...prev,
      [questionId]: answer
    }));
  };
  const checkAnswers = () => {
    if (!exercise || !exercise.content.questions) return;
    setShowResults(true);
    // Stop timer
    if (timer) clearInterval(timer);
    // Calculate score
    const correctAnswers = exercise.content.questions.reduce((count: number, question: any) => {
      return userAnswers[question.id] === question.correctAnswer || userAnswers[question.id]?.toLowerCase() === question.answer?.toLowerCase() ? count + 1 : count;
    }, 0);
    const score = Math.round(correctAnswers / exercise.content.questions.length * 100);
    // Update progress
    setExercise(prev => ({
      ...prev,
      progress: score
    }));
    // Show toast with results
    if (score === 100) {
      toast.success(isArabic ? 'أحسنت!' : 'Perfect score!', isArabic ? 'لقد أجبت على جميع الأسئلة بشكل صحيح.' : 'You answered all questions correctly.');
    } else if (score >= 70) {
      toast.success(isArabic ? 'عمل جيد!' : 'Good job!', isArabic ? `لقد أجبت على ${correctAnswers} من أصل ${exercise.content.questions.length} بشكل صحيح.` : `You got ${correctAnswers} out of ${exercise.content.questions.length} correct.`);
    } else {
      toast.info(isArabic ? 'استمر في المحاولة' : 'Keep practicing', isArabic ? `لقد أجبت على ${correctAnswers} من أصل ${exercise.content.questions.length} بشكل صحيح.` : `You got ${correctAnswers} out of ${exercise.content.questions.length} correct.`);
    }
  };
  const resetExercise = () => {
    setUserAnswers({});
    setShowResults(false);
    setCurrentQuestionIndex(0);
    setTimeSpent(0);
    // Restart timer
    if (timer) clearInterval(timer);
    const intervalId = setInterval(() => {
      setTimeSpent(prev => prev + 1);
    }, 1000);
    setTimer(intervalId);
  };
  const toggleBookmark = () => {
    setIsBookmarked(!isBookmarked);
    if (!isBookmarked) {
      toast.success(isArabic ? 'تمت الإضافة للمفضلة' : 'Added to bookmarks', isArabic ? 'يمكنك العثور على هذا التمرين في قسم المفضلة.' : 'You can find this exercise in your bookmarks section.');
    } else {
      toast.info(isArabic ? 'تمت الإزالة من المفضلة' : 'Removed from bookmarks');
    }
  };
  const goToNextQuestion = () => {
    if (!exercise || !exercise.content.questions) return;
    if (currentQuestionIndex < exercise.content.questions.length - 1) {
      setCurrentQuestionIndex(currentQuestionIndex + 1);
    }
  };
  const goToPreviousQuestion = () => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex(currentQuestionIndex - 1);
    }
  };
  const formatTime = (seconds: number) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };
  if (loading || !exercise) {
    return <div className="w-full px-6 md:px-10 lg:px-20 py-16" data-id="element-2790">
        <div className="max-w-7xl mx-auto" data-id="element-2791">
          <div className="animate-pulse" data-id="element-2792">
            <div className="h-10 bg-gray-200 dark:bg-gray-700 rounded-md mb-4 w-1/3" data-id="element-2793"></div>
            <div className="h-6 bg-gray-200 dark:bg-gray-700 rounded-md mb-8 w-1/2" data-id="element-2794"></div>
            <div className="h-64 bg-gray-200 dark:bg-gray-700 rounded-xl mb-4" data-id="element-2795"></div>
            <div className="h-12 bg-gray-200 dark:bg-gray-700 rounded-md mb-4 w-1/4" data-id="element-2796"></div>
            <div className="grid grid-cols-2 gap-4 mb-8" data-id="element-2797">
              <div className="h-12 bg-gray-200 dark:bg-gray-700 rounded-md" data-id="element-2798"></div>
              <div className="h-12 bg-gray-200 dark:bg-gray-700 rounded-md" data-id="element-2799"></div>
            </div>
          </div>
        </div>
      </div>;
  }
  const renderExerciseContent = () => {
    if (exercise.content.type === 'word-matching') {
      return <WordMatchingGame data-id="element-2800" />;
    } else if (exercise.content.type === 'flashcards') {
      return <FlashcardPractice cards={[{
        id: 1,
        en: 'Hello',
        ar: 'مرحباً',
        pronunciation: 'hə-ˈlō',
        difficulty: 'easy'
      }, {
        id: 2,
        en: 'Thank you',
        ar: 'شكراً',
        pronunciation: 'thank you',
        difficulty: 'easy'
      }, {
        id: 3,
        en: 'Goodbye',
        ar: 'وداعاً',
        pronunciation: 'ˈgud-ˌbī',
        difficulty: 'easy'
      }, {
        id: 4,
        en: 'Please',
        ar: 'من فضلك',
        pronunciation: 'plēz',
        difficulty: 'easy'
      }, {
        id: 5,
        en: 'Yes',
        ar: 'نعم',
        pronunciation: 'yes',
        difficulty: 'easy'
      }]} data-id="element-2801" />;
    } else if (exercise.content.type === 'counting-quiz') {
      return <CountingQuiz data-id="element-2802" />;
    } else if (exercise.content.questions) {
      const question = exercise.content.questions[currentQuestionIndex];
      const isMultipleChoice = question.options !== undefined;
      return <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md p-6 mb-8" data-id="element-2803">
          <div className="flex justify-between items-center mb-6" data-id="element-2804">
            <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400" data-id="element-2805">
              <ClockIcon className="w-4 h-4" data-id="element-2806" />
              <span data-id="element-2807">{formatTime(timeSpent)}</span>
            </div>
            <div className="flex items-center gap-2 text-sm" data-id="element-2808">
              <span className="text-gray-500 dark:text-gray-400" data-id="element-2809">
                {isArabic ? 'السؤال' : 'Question'} {currentQuestionIndex + 1}/
                {exercise.content.questions.length}
              </span>
            </div>
          </div>
          <div className="mb-8" data-id="element-2810">
            <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-6" data-id="element-2811">
              {isArabic ? question.question.ar : question.question.en}
            </h3>
            {isMultipleChoice ? <div className="space-y-4" data-id="element-2812">
                {question.options.map((option: any) => <button key={option.id} onClick={() => handleAnswerSelect(question.id, option.id)} disabled={showResults} className={`
                      w-full p-4 rounded-lg text-left transition-all
                      ${userAnswers[question.id] === option.id ? showResults ? option.id === question.correctAnswer ? 'bg-green-100 dark:bg-green-900/30 border border-green-500 dark:border-green-500' : 'bg-red-100 dark:bg-red-900/30 border border-red-500 dark:border-red-500' : 'bg-blue-100 dark:bg-blue-900/30 border border-blue-500 dark:border-blue-500' : 'bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700'}
                    `} data-id="element-2813">
                    <div className="flex items-center justify-between" data-id="element-2814">
                      <div data-id="element-2815">
                        <span className="font-medium mr-2" data-id="element-2816">
                          {option.id.toUpperCase()}.
                        </span>
                        <span data-id="element-2817">
                          {isArabic ? option.text.ar : option.text.en}
                        </span>
                      </div>
                      {showResults && option.id === question.correctAnswer && <CheckCircleIcon className="w-5 h-5 text-green-500 dark:text-green-400" data-id="element-2818" />}
                      {showResults && userAnswers[question.id] === option.id && option.id !== question.correctAnswer && <XIcon className="w-5 h-5 text-red-500 dark:text-red-400" data-id="element-2819" />}
                    </div>
                  </button>)}
              </div> : <div data-id="element-2820">
                <input type="text" value={userAnswers[question.id] || ''} onChange={e => handleTextAnswerChange(question.id, e.target.value)} disabled={showResults} placeholder={isArabic ? 'اكتب إجابتك هنا...' : 'Type your answer here...'} className="w-full p-4 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400" data-id="element-2821" />
                {showResults && <div className={`mt-4 p-4 rounded-lg ${userAnswers[question.id]?.toLowerCase() === question.answer?.toLowerCase() ? 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300' : 'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300'}`} data-id="element-2822">
                    <div className="font-medium mb-1" data-id="element-2823">
                      {userAnswers[question.id]?.toLowerCase() === question.answer?.toLowerCase() ? isArabic ? 'إجابة صحيحة!' : 'Correct answer!' : isArabic ? 'الإجابة الصحيحة:' : 'Correct answer:'}{' '}
                      {question.answer}
                    </div>
                    <div className="text-sm" data-id="element-2824">
                      {isArabic ? question.explanation?.ar : question.explanation?.en}
                    </div>
                  </div>}
              </div>}
          </div>
          <div className="flex justify-between" data-id="element-2825">
            <button onClick={goToPreviousQuestion} disabled={currentQuestionIndex === 0} className={`
                flex items-center gap-2 px-4 py-2 rounded-lg transition-all
                ${currentQuestionIndex === 0 ? 'bg-gray-100 dark:bg-gray-800 text-gray-400 dark:text-gray-600 cursor-not-allowed' : 'bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'}
              `} data-id="element-2826">
              <ArrowLeftIcon className="w-4 h-4" data-id="element-2827" />
              <span data-id="element-2828">{isArabic ? 'السابق' : 'Previous'}</span>
            </button>
            {currentQuestionIndex === exercise.content.questions.length - 1 ? !showResults ? <button onClick={checkAnswers} className="flex items-center gap-2 px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors" data-id="element-2829">
                  <span data-id="element-2830">{isArabic ? 'تحقق من الإجابات' : 'Check Answers'}</span>
                </button> : <button onClick={resetExercise} className="flex items-center gap-2 px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors" data-id="element-2831">
                  <div className="w-4 h-4" data-id="element-2832" />
                  <span data-id="element-2833">{isArabic ? 'إعادة المحاولة' : 'Try Again'}</span>
                </button> : <button onClick={goToNextQuestion} className="flex items-center gap-2 px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors" data-id="element-2834">
                <span data-id="element-2835">{isArabic ? 'التالي' : 'Next'}</span>
                <ArrowRightIcon className="w-4 h-4" data-id="element-2836" />
              </button>}
          </div>
        </div>;
    }
    return null;
  };
  return <div className="w-full px-6 md:px-10 lg:px-20 py-16" data-id="element-2837">
      <Sitemap currentPage={{
      en: exercise.title.en,
      ar: exercise.title.ar
    }} breadcrumbs={[{
      label: {
        en: 'Learn',
        ar: 'تعلم'
      },
      href: '/learn'
    }, {
      label: {
        en: 'Practice Exercises',
        ar: 'تمارين تطبيقية'
      },
      href: '/learn/practice'
    }]} data-id="element-2838" />
      <div className="max-w-7xl mx-auto" data-id="element-2839">
        <div className="mb-8" data-id="element-2840">
          <div className="flex items-center justify-between mb-4" data-id="element-2841">
            <div className="flex items-center gap-3" data-id="element-2842">
              <BookIcon className="w-8 h-8 text-blue-600 dark:text-blue-400" data-id="element-2843" />
              <h1 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white" data-id="element-2844">
                {isArabic ? exercise.title.ar : exercise.title.en}
              </h1>
            </div>
            <Tooltip content={isArabic ? 'إضافة للمفضلة' : 'Add to bookmarks'} data-id="element-2845">
              <button onClick={toggleBookmark} className={`p-2 rounded-full transition-colors ${isBookmarked ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400' : 'hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-500 dark:text-gray-400'}`} aria-label={isBookmarked ? 'Remove bookmark' : 'Add bookmark'} data-id="element-2846">
                <BookmarkIcon className="w-5 h-5" data-id="element-2847" />
              </button>
            </Tooltip>
          </div>
          <p className="text-xl text-gray-600 dark:text-gray-300 mb-6" data-id="element-2848">
            {isArabic ? exercise.description.ar : exercise.description.en}
          </p>
          <div className="flex flex-wrap gap-4 mb-6" data-id="element-2849">
            <div className="flex items-center gap-2 px-3 py-1 bg-gray-100 dark:bg-gray-800 rounded-full" data-id="element-2850">
              <span className="text-sm font-medium text-gray-700 dark:text-gray-300" data-id="element-2851">
                {isArabic ? exercise.difficultyAr : exercise.difficulty}
              </span>
            </div>
            <div className="flex items-center gap-2 px-3 py-1 bg-gray-100 dark:bg-gray-800 rounded-full" data-id="element-2852">
              <span className="text-sm font-medium text-gray-700 dark:text-gray-300" data-id="element-2853">
                {isArabic ? exercise.typeAr : exercise.type}
              </span>
            </div>
            <div className="flex items-center gap-2 px-3 py-1 bg-gray-100 dark:bg-gray-800 rounded-full" data-id="element-2854">
              <ClockIcon className="w-4 h-4 text-gray-500 dark:text-gray-400" data-id="element-2855" />
              <span className="text-sm font-medium text-gray-700 dark:text-gray-300" data-id="element-2856">
                {exercise.timeEstimate} {isArabic ? 'دقيقة' : 'mins'}
              </span>
            </div>
          </div>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md p-6 mb-8" data-id="element-2857">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4" data-id="element-2858">
            <div data-id="element-2859">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-1" data-id="element-2860">
                {isArabic ? 'تقدمك في هذا التمرين' : 'Your progress on this exercise'}
              </h2>
              <p className="text-sm text-gray-600 dark:text-gray-300" data-id="element-2861">
                {isArabic ? 'أكمل التمرين لتتبع تقدمك' : 'Complete the exercise to track your progress'}
              </p>
            </div>
            <div className="w-full sm:w-48" data-id="element-2862">
              <div className="flex items-center justify-between text-sm mb-1" data-id="element-2863">
                <span className="text-gray-600 dark:text-gray-400" data-id="element-2864">
                  {exercise.progress}%
                </span>
                <span className="text-gray-600 dark:text-gray-400" data-id="element-2865">
                  {isArabic ? 'مكتمل' : 'Complete'}
                </span>
              </div>
              <div className="h-2 bg-gray-100 dark:bg-gray-700 rounded-full overflow-hidden" data-id="element-2866">
                <div className="h-full bg-blue-600 dark:bg-blue-400 transition-all duration-300" style={{
                width: `${exercise.progress}%`
              }} data-id="element-2867" />
              </div>
            </div>
          </div>
        </div>
        {renderExerciseContent()}
        <div className="mt-8 flex justify-center" data-id="element-2868">
          <button onClick={() => navigate('/learn/practice')} className="flex items-center gap-2 px-6 py-3 bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors" data-id="element-2869">
            <ArrowLeftIcon className="w-5 h-5" data-id="element-2870" />
            <span data-id="element-2871">
              {isArabic ? 'العودة إلى التمارين' : 'Back to Exercises'}
            </span>
          </button>
        </div>
      </div>
    </div>;
}